import styled from "@emotion/styled";
import { DecileClassification } from "../../topProductsTable/StarRating";
import Table, { type TableDatum } from "../../topProductsTable/Table";
import { index, scaleThreshold } from "d3";
import type { ScaleThreshold } from "d3-scale";
import type { HierarchyNode } from "d3";
import { useParentSize } from "@visx/responsive";
import { useMemo } from "react";
import { GraphLoading } from "@atlas-int/viz/shared";
import { getClassificationServicesCategory, ProductClass } from "@atlas-int/data";

const hs22ServicesCategory = getClassificationServicesCategory(ProductClass.HS22Products);
import type { Cpy_GrowthQuery } from "@atlas-int/data/graphql";

/** Narrow CPY row from the growth query. */
type GrowthCPY = NonNullable<
  NonNullable<Cpy_GrowthQuery["countryProductYear"]>[number]
>;

/** Narrow PY row from the growth query. */
type GrowthPY = NonNullable<
  NonNullable<Cpy_GrowthQuery["productYear"]>[number]
>;

/** Narrow CY row from the growth query. */
type GrowthCY = NonNullable<
  NonNullable<Cpy_GrowthQuery["countryYear"]>[number]
>;

/** Narrow threshold row from the growth query. */
type GrowthCYT = NonNullable<
  NonNullable<Cpy_GrowthQuery["countryYearThresholds"]>[number]
>;

/** Metadata node in the product hierarchy tree. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MetaDataNode = HierarchyNode<any>;

/** Lookup map: productId -> metadata hierarchy node. */
type MetaDataLookup = Map<string, MetaDataNode>;

/** Lookup map: productId -> product-year row. */
type ProductYearLookup = Map<string, GrowthPY>;

const gridLines = {
  graphTop: "countryProfilesGraphTop",
  graphBottom: "countryProfilesGraphBottom",

  graphContentLeft: "countryProfilesGraphContentLeft",
  graphContentRight: "countryProfilesGraphContentRight",
};

export const GraphAreaContainer = styled.div`
  grid-row: ${gridLines.graphTop} / ${gridLines.graphBottom};
  grid-column: ${gridLines.graphContentLeft} / ${gridLines.graphContentRight};
  position: relative;
`;
const diamondSizes = [
  DecileClassification.Top,
  DecileClassification.Top,
  DecileClassification.Ninth,
  DecileClassification.Eighth,
  DecileClassification.Seventh,
  DecileClassification.Sixth,
  DecileClassification.Fifth,
  DecileClassification.Fourth,
  DecileClassification.Third,
  DecileClassification.Second,
  DecileClassification.Last,
  DecileClassification.Empty,
  DecileClassification.Empty,
].reverse();

const thresholdsToArr = (t: GrowthCYT | undefined): number[] => {
  const { variable, __typename, ...rest } = t || ({} as Partial<GrowthCYT>);
  return Object.values(rest).filter((v): v is number => v != null);
};

interface TableScales {
  complexityScale: ScaleThreshold<number, DecileClassification>;
  opportunityScale: ScaleThreshold<number, DecileClassification>;
  feasibilityScale: ScaleThreshold<number, DecileClassification>;
  rcaScale: ScaleThreshold<number, DecileClassification>;
}

export const useTableScales = (countryYearThresholds: GrowthCYT[]): TableScales =>
  useMemo(() => {
    const thresholdLookup = index(
      countryYearThresholds,
      (d) => d.variable as string,
    );

    const complexityScale = scaleThreshold<number, DecileClassification>()
      .domain(thresholdsToArr(thresholdLookup.get("pci")))
      .range(diamondSizes);

    const feasibilityScale = scaleThreshold<number, DecileClassification>()
      .domain(thresholdsToArr(thresholdLookup.get("normalized_distance")))
      .range(diamondSizes);

    const opportunityScale = scaleThreshold<number, DecileClassification>()
      .domain(thresholdsToArr(thresholdLookup.get("normalized_cog")))
      .range(diamondSizes);

    const rcaScale = scaleThreshold<number, DecileClassification>()
      .domain(thresholdsToArr(thresholdLookup.get("normalized_export_rca")))
      .range(diamondSizes);
    return { complexityScale, opportunityScale, feasibilityScale, rcaScale };
  }, [countryYearThresholds]);

interface ProductsTableProps {
  displayData: Cpy_GrowthQuery | undefined;
  countryProductYear: GrowthCPY[];
  productYear: GrowthPY[];
  loading: boolean;
  countryData: GrowthCY | undefined;
  metaDataLookup: MetaDataLookup;
  metaDataTree: MetaDataNode;
  colorMap: Map<string, string>;
  countryLookup: Map<string, unknown>;
  hiddenCategories: string[];
  countryYearThresholds: GrowthCYT[];
  productClass: string;
  year: number | undefined;
}

const ProductsTable = ({
  displayData: _displayData,
  countryProductYear,
  productYear,
  loading,
  countryData: _countryData,
  metaDataLookup,
  metaDataTree: _metaDataTree,
  colorMap,
  countryLookup: _countryLookup,
  hiddenCategories,
  countryYearThresholds,
  productClass,
  year,
}: ProductsTableProps) => {
  const filteredData = countryProductYear.filter((d) => {
    if (!d.normalizedPci) return false;

    // Exclude services (sector 14) for HS22 - services shouldn't be in growth potential table
    const topParentId = metaDataLookup.get(d.productId as string)?.data
      ?.topParent?.productId;
    if (productClass === "HS22" && topParentId === hs22ServicesCategory) {
      return false;
    }

    return true;
  });
  const totalExportLookup: ProductYearLookup = index(
    productYear,
    (d) => d.productId as string,
  ) as ProductYearLookup;
  const { parentRef, width, height } = useParentSize({ debounceTime: 150 });
  const { complexityScale, opportunityScale, feasibilityScale, rcaScale } =
    useTableScales(countryYearThresholds);

  const tableData = filteredData
    .map((d) => {
      const pid = d.productId as string;
      const pyRow = totalExportLookup.get(pid);
      return {
        ...d,
        id: pid,
        color:
          colorMap.get(
            metaDataLookup.get(pid)?.data?.topParent?.productId ?? "",
          ) ?? "",
        code: metaDataLookup.get(pid)?.data?.code ?? "",
        name: metaDataLookup.get(pid)?.data?.nameShortEn ?? "",
        complexityDecile: complexityScale(Number(d.normalizedPci)),
        opportunityGainDecile: opportunityScale(Number(d.normalizedCog)),
        rcaDecile: rcaScale(Number(d.exportRca)),
        feasibilityDecile: feasibilityScale(Number(d.normalizedDistance)),
        globalExportValue: pyRow?.exportValue ?? 0,
        normalizedPci: Number(d.normalizedPci),
        normalizedDistance: Number(d.normalizedDistance),
        normalizedCog: Number(d.normalizedCog),
        exportRca: Number(d.exportRca),
        normalizedFeasibility: Number(d.normalizedDistance),
        normalizedOpportunityGain: Number(d.normalizedCog),
        globalExportValueChange: pyRow?.exportValueGrowth5 ?? 0,
        //balanced approach
        score:
          0.35 * Number(d.normalizedCog) +
          0.15 * Number(d.normalizedPci) +
          0.5 * Number(d.normalizedDistance),
      };
    })
    .sort((d1, d2) => d2.score - d1.score)
    .filter(
      (d) =>
        !hiddenCategories.includes(
          metaDataLookup.get(d.id)?.data?.topParent?.productId ?? "",
        ),
    );

  return (
    <div style={{ height: "100%", width: "100%" }} ref={parentRef}>
      {loading && <GraphLoading zIndex={1000} />}
      <Table
        products={tableData as TableDatum[]}
        getFluentString={() => "hello"}
        width={width}
        height={height}
        zIndexFromParent={20}
        year={year}
      />
    </div>
  );
};

export default ProductsTable;
